import './age-restriction-modal.scss'
import type { AgeRestrictionModalPropsApi } from '@common/components/ageRestrictionModal/models'
import { getSessionStorage } from '@common/utils/session/getSessionStorage'
import { setSessionStorage } from '@common/utils/session/setSessionStorage'
import { urlFriendly } from '@common/utils/urls/urlFriendly'
import type { FC } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useBlocker, useLocation } from 'react-router-dom-v7'
import { cmsDataService } from '@/content/services/cms.data.service'
import { navigationService } from '@/content/services/navigation.service'
import { AgeRestrictionModalConfirmation } from './subcomponents/AgeRestrictionModalConfirmation'

const Modal: FC<AgeRestrictionModalPropsApi> = props => {
    const {
        ageQuestionMessage,
        yesResponseButtonLabel,
        noResponseButtonLabel,
        underageResponseMessage,
        closeButtonLabel,
        affectedPages = [],
    } = props

    const location = useLocation()
    const [isDirectPageEntryRestricted, setIsDirectPageEntryRestricted] = useState(false)

    const isApplicable = useCallback(
        (pathname: string) => {
            const sessionKey = urlFriendly(`confirmed-page-${pathname}`)
            const pageConfirmed = getSessionStorage(sessionKey) === 'true'
            return affectedPages.includes(pathname) && !pageConfirmed
        },
        [affectedPages]
    )

    const blocker = useBlocker(({ nextLocation }) => {
        return isApplicable(nextLocation.pathname)
    })

    // Handle direct page entry
    useEffect(() => {
        if (isApplicable(location.pathname)) {
            setIsDirectPageEntryRestricted(true)
        }
    }, [location.pathname, affectedPages, isApplicable])

    const close = () => {
        setIsDirectPageEntryRestricted(false)
        blocker.reset?.()
    }

    const onClose = () => {
        if (isDirectPageEntryRestricted) {
            navigationService.goToHome()
        }
        close()
    }

    const onConfirm = () => {
        const pathname = blocker?.location?.pathname || location.pathname
        const sessionKey = urlFriendly(`confirmed-page-${pathname}`)
        setSessionStorage(sessionKey, 'true')
        setIsDirectPageEntryRestricted(false)
        blocker?.proceed?.()
        close()
    }

    const show = isDirectPageEntryRestricted || blocker.state === 'blocked'

    return (
        <AgeRestrictionModalConfirmation
            show={show}
            displayText={{
                questionMessage: ageQuestionMessage,
                confirmButtonLabel: yesResponseButtonLabel,
                declineButtonLabel: noResponseButtonLabel,
                declineMessage: underageResponseMessage,
                declineClosedButtonLabel: closeButtonLabel,
            }}
            events={{
                onConfirmButtonClicked: onConfirm,
                onDeclinedCloseButtonClicked: onClose,
            }}
        />
    )
}

const AgeRestrictionModal = () => {
    const { ageRestrictionModal } = cmsDataService.get()?.ageRestrictionWarningConfiguration || {}
    return ageRestrictionModal ? <Modal {...ageRestrictionModal} /> : null
}

export default AgeRestrictionModal

AgeRestrictionModal.displayName = 'AgeRestrictionModal'
